import { SerializedError } from '@reduxjs/toolkit'
import { REQUEST_TIMEOUT_MESSAGE } from 'src/constants/request.constants'

/**
 * Cancellation registry for the heavy "all community plans" prefetch.
 *
 * `getPaginatedPlans` / `getPaginatedDrafts` fan out many paginated requests
 * to `/plan/all-community-plans` while the user is in Create mode. Submitted
 * and draft prefetches run against *separate* controllers so that:
 *  - a submitted refresh can invalidate only the previous submitted fan-out
 *    (without disturbing an in-flight draft prefetch, and vice versa), and
 *  - leaving Create mode can still abort everything via
 *    `cancelAllCommunityPlansRequests()`.
 */

const REQUEST_CANCELLED_CODE = 'ERR_CANCELED'
const REQUEST_CANCELLED_NAME = 'CanceledError'
const ABORT_ERROR_NAME = 'AbortError'
const REQUEST_TIMED_OUT_NAME = 'TimeoutError'

let submittedPlansController: AbortController | null = null
let draftPlansController: AbortController | null = null

/**
 * Returns the signal for the current submitted all-community-plans fan-out,
 * lazily creating a fresh controller when none is active (e.g. after a cancel
 * or on the first prefetch). All requests in a batch share this signal so a
 * single `abort()` cancels the whole fan-out.
 */
export const getSubmittedPlansSignal = (): AbortSignal => {
  if (!submittedPlansController) {
    submittedPlansController = new AbortController()
  }
  return submittedPlansController.signal
}

/**
 * Returns the signal for the current draft all-community-plans fan-out,
 * lazily creating a fresh controller when none is active.
 */
export const getDraftPlansSignal = (): AbortSignal => {
  if (!draftPlansController) {
    draftPlansController = new AbortController()
  }
  return draftPlansController.signal
}

/**
 * Aborts every in-flight submitted all-community-plans request and clears the
 * controller so the next prefetch starts with a fresh, un-aborted signal.
 */
export const cancelSubmittedPlansRequests = (): void => {
  submittedPlansController?.abort()
  submittedPlansController = null
}

/**
 * Aborts every in-flight draft all-community-plans request and clears the
 * controller so the next prefetch starts with a fresh, un-aborted signal.
 */
export const cancelDraftPlansRequests = (): void => {
  draftPlansController?.abort()
  draftPlansController = null
}

/**
 * Aborts both the submitted and draft fan-outs. Used when leaving Create mode,
 * where none of the prefetched data is needed any more.
 */
export const cancelAllCommunityPlansRequests = (): void => {
  cancelSubmittedPlansRequests()
  cancelDraftPlansRequests()
}

/**
 * True when an error represents an aborted/cancelled request. Aborting a native
 * `fetch` via an `AbortController` rejects with a `DOMException` whose
 * `name` is `'AbortError'`, which survives RTK's `miniSerializeError`. The
 * legacy axios `CanceledError` shape (`name: 'CanceledError'`,
 * `code: 'ERR_CANCELED'`) is still recognized for backwards compatibility, so
 * this works for the raw error thrown inside a thunk and for the serialized
 * form stored on `action.error` in a rejected reducer.
 */
export const isRequestCancelledError = (
  error: unknown | SerializedError
): boolean => {
  const candidate = error as Partial<SerializedError> | undefined
  return (
    candidate?.code === REQUEST_CANCELLED_CODE ||
    candidate?.name === REQUEST_CANCELLED_NAME ||
    candidate?.name === ABORT_ERROR_NAME
  )
}

export interface RequestTimeout {
  /** Stops the timer; call once the request settles. */
  clear: () => void
  /** True once the deadline passed and the signal was aborted. */
  hasElapsed: () => boolean
  signal: AbortSignal
}

/**
 * A signal that aborts once `milliseconds` have passed, for requests that must
 * not leave the UI waiting indefinitely. Behaves like `AbortSignal.timeout`:
 * the abort reason is a `DOMException` named `'TimeoutError'`, which is what
 * `fetch` rejects with in browsers that honour abort reasons. Browsers that do
 * not still reject with `'AbortError'`, so the owner of the timeout should also
 * check `hasElapsed()` rather than the error name alone.
 */
export const createRequestTimeout = (milliseconds: number): RequestTimeout => {
  const controller = new AbortController()
  const timer = setTimeout(() => {
    controller.abort(
      new DOMException(REQUEST_TIMEOUT_MESSAGE, REQUEST_TIMED_OUT_NAME)
    )
  }, milliseconds)

  return {
    clear: () => clearTimeout(timer),
    hasElapsed: () => controller.signal.aborted,
    signal: controller.signal,
  }
}

/**
 * True when an error is the `TimeoutError` a request aborted by
 * `createRequestTimeout` (or `AbortSignal.timeout`) rejects with. Like
 * `isRequestCancelledError`, this reads `name`, which survives RTK's
 * `miniSerializeError`.
 */
export const isRequestTimedOutError = (
  error: unknown | SerializedError
): boolean => {
  const candidate = error as Partial<SerializedError> | undefined
  return candidate?.name === REQUEST_TIMED_OUT_NAME
}
